// HM interview selection (Step 9, plan D15 [r3]): the Hiring Manager of the requisition decides a candidate who is
// *Pending HM Selection* with the same Advance · Hold · Pass control as the slate review, bound to V1 `screen.decide`
// ("Pending HM Selection -> Advance/Hold/Pass. Advanced -> HM Interview Scheduling Requested."). After Advance the card
// reads *HM Interview Scheduling Requested* (the scheduling engine's own status words replace it once it is present).
// The Recruiter and the HR Business Partner read the status only; an Interviewer sees nothing. This card is the step's
// only decision: Step 9 reuses Step 7's control (A71, DC-106), so V1's own Advance, Hold and Pass buttons
// and their dialogs are retired from the screen section below.

import { useState } from "react";
import { candidateOpen, screenDecide } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { ACTION_LABELS, Button, Card, Flex, HM_SELECTION_STATUS, Pill, RULE_LABELS, Stack } from "@/mc";
import { useSettledRead } from "@/views/shared/settledRead";
import { usePassReasonChoices } from "@/views/candidate/options";
import { DecisionControls } from "@/views/pipeline/Decision";
import { PIPELINE_LABELS } from "@/views/pipeline/labels";
import { EMPTY_DECISION, decisionBlock, type DecisionDraft } from "@/views/pipeline/pipeline";
import { pipelineRefusal } from "@/views/pipeline/refusals";
import { useSession } from "@/session/useSession";
import { CardReadSlot, cardReadState } from "./CardRead";

/** The projections that read the step's status (the owner, the HM, the HR Business Partner); an Interviewer's does not. */
const READERS = new Set(["identity", "review", "hrbp"]);

/** The skeleton's height: the card with the decision controls, its tallest form. */
const CARD_HEIGHT = 220;

export function HmSelectionCard({ rid, aid }: { rid: string; aid: string }) {
  const { can } = useSession();
  const app = useSettledRead(["application", aid, rid], () => candidateOpen({ requisition_id: rid, application_id: aid }));
  const [draft, setDraft] = useState<DecisionDraft>(EMPTY_DECISION);
  const [refused, setRefused] = useState<string | null>(null);
  const passReasons = usePassReasonChoices();
  const decide = useWrite(
    (input: Parameters<typeof screenDecide>[0], key) => screenDecide(input, key),
    [["application", aid], ["screen", aid], ["candidates", rid], ["pipeline"], ["round", rid, aid], ["today"]],
  );
  const state = cardReadState(app);
  if (state !== "ready" || !app.data) {
    return (
      <CardReadSlot
        state={state} title={PIPELINE_LABELS.hmInterviewSelection} icon="users" height={CARD_HEIGHT} error={app.error}
        onRetry={() => void app.refetch()} retrying={app.isFetching} 
      />
    );
  }
  const data = app.data;
  if (!READERS.has(data.projection ?? "")) return null;
  const stage = data.card.stage;
  const selected = stage === "hm_round" && "decisions" in data && data.decisions.some((d) => d["kind"] === "screen_selection" && d["value"] === "advance");
  if (stage !== "screened" && !selected) return null;
  const decides = stage === "screened" && data.projection === "review" && can("screen.decide");
  const why = decisionBlock(draft, false);
  const submit = () => {
    if (!draft.value) return;
    setRefused(null);
    decide
      .mutateAsync({
        requisition_id: rid,
        application_id: aid,
        value: draft.value,
        reason_text: draft.notes.trim(),
        reason_code: draft.value === "pass" ? draft.reason : null,
      })
      .catch((error: unknown) => setRefused(pipelineRefusal(error) ?? RULE_LABELS.failedSyncRetryable));
  };
  const pill = selected
    ? <Pill tone="ok">{HM_SELECTION_STATUS.hmInterviewSchedulingRequested}</Pill>
    : <Pill tone="info">{HM_SELECTION_STATUS.pendingHmSelection}</Pill>;
  return (
    <Card title={PIPELINE_LABELS.hmInterviewSelection} icon="users" pills={pill} refused={refused ? { reason: refused } : undefined}>
      {decides ? (
        <Stack gap={12}>
          <DecisionControls draft={draft} onChange={setDraft} holdReasons={[]} passReasons={passReasons} scope="select" busy={decide.isPending} />
          <Flex justify="end">
            <Button tone="primary" blocked={why ? { reason: why } : undefined} busy={decide.isPending} onClick={submit} >
              {ACTION_LABELS.submit}
            </Button>
          </Flex>
        </Stack>
      ) : null}
    </Card>
  );
}
